/**
 * AI Clipper Metrics data helpers.
 * Backend resolvers (aiClipperMetricsSummary / aiClipperUserBreakdown /
 * aiClipperTimeSeries) read from ai_clip_candidate and aggregate counts.
 * JWT flows through the app Apollo client's authLink (configured in App.tsx).
 */
import { ApolloClient } from '@apollo/client';
import {
  GET_AI_CLIPPER_METRICS_SUMMARY,
  GET_AI_CLIPPER_USER_BREAKDOWN,
  GET_AI_CLIPPER_TIME_SERIES,
} from './queries';
import {
  AiClipperMetricsFilter,
  AiClipperMetricsSummary,
  AiClipperUserBreakdownPage,
  AiClipperTimeSeriesGranularity,
  AiClipperTimeSeriesResult,
} from './types';

type AppApolloClient = ApolloClient<any>;

function normalizeFilter(
  filter: AiClipperMetricsFilter
): AiClipperMetricsFilter | undefined {
  const out: AiClipperMetricsFilter = {};
  const slug = filter.athleteSlug?.trim();
  const name = filter.athleteName?.trim();
  if (slug) out.athleteSlug = slug;
  if (name) out.athleteName = name;
  if (filter.fromDate) out.fromDate = filter.fromDate;
  if (filter.toDate) out.toDate = filter.toDate;
  if (filter.statuses?.length) out.statuses = filter.statuses;
  if (filter.sourceTypes?.length) out.sourceTypes = filter.sourceTypes;
  if (filter.approvalSources?.length)
    out.approvalSources = filter.approvalSources;
  return Object.keys(out).length ? out : undefined;
}

export async function fetchMetricsSummary(
  client: AppApolloClient,
  filter: AiClipperMetricsFilter
): Promise<AiClipperMetricsSummary> {
  const result = await client.query<{
    aiClipperMetricsSummary: AiClipperMetricsSummary;
  }>({
    query: GET_AI_CLIPPER_METRICS_SUMMARY,
    variables: { filter: normalizeFilter(filter) },
    fetchPolicy: 'network-only',
  });
  return (
    result.data?.aiClipperMetricsSummary ?? {
      totalGenerated: 0,
      accepted: 0,
      manualAccepted: 0,
      systemAccepted: 0,
      rejected: 0,
      notReviewed: 0,
      acceptRatePctOfReviewed: null,
      sourceBreakdown: [],
    }
  );
}

export interface FetchBreakdownOptions {
  filter: AiClipperMetricsFilter;
  limit: number;
  offset: number;
  direction?: 'ASC' | 'DESC';
}

export async function fetchUserBreakdown(
  client: AppApolloClient,
  options: FetchBreakdownOptions
): Promise<AiClipperUserBreakdownPage> {
  const result = await client.query<{
    aiClipperUserBreakdown: AiClipperUserBreakdownPage;
  }>({
    query: GET_AI_CLIPPER_USER_BREAKDOWN,
    variables: {
      input: {
        filter: normalizeFilter(options.filter),
        limit: options.limit,
        offset: options.offset,
        direction: options.direction ?? 'DESC',
      },
    },
    fetchPolicy: 'network-only',
  });
  return (
    result.data?.aiClipperUserBreakdown ?? {
      entities: [],
      total: 0,
      offset: options.offset,
      limit: options.limit,
    }
  );
}

export async function fetchTimeSeries(
  client: AppApolloClient,
  options: {
    filter: AiClipperMetricsFilter;
    granularity: AiClipperTimeSeriesGranularity;
  }
): Promise<AiClipperTimeSeriesResult> {
  const result = await client.query<{
    aiClipperTimeSeries: AiClipperTimeSeriesResult;
  }>({
    query: GET_AI_CLIPPER_TIME_SERIES,
    variables: {
      input: {
        granularity: options.granularity,
        filter: normalizeFilter(options.filter),
      },
    },
    fetchPolicy: 'network-only',
  });
  return result.data?.aiClipperTimeSeries ?? { points: [], truncated: false };
}
